import {Component, EventEmitter, Input, OnChanges, OnInit, Output} from '@angular/core';
import {ConfirmationService, SelectItem} from 'primeng/api';
import {OverlayPanel} from "primeng/overlaypanel";
import {forkJoin} from 'rxjs';
import {FormBuilder} from '@angular/forms';

import {GrowlService, HttpErrorMessageService, UserService} from '@app/core';
import {DocFile} from "@app/shared/classes/doc-file";
import {PartnerCreditCard} from "@app/shared/classes/partner-credit-card";
import {OrgAchDetail} from "@app/shared/classes/org-ach-detail";
import {AchMicroDepositVerification, PartnerAchDetail} from "@app/shared/classes/partner-ach-detail";
import {OrgCreditCard} from "@app/shared/classes/org-credit-card";
import {OrgType, PaymentTerm, TradingPartner} from '@app/shared/classes/trading-partner';
import {PartnerPaymentSetting} from "@app/shared/classes/partner-payment-setting";

import {ApprovalStatusType} from '@app/shared/enums/approval-status-type.enum';
import {CheckPaymentType} from '@app/shared/enums/check-payment-type.enum';
import {DocumentType} from '@app/shared/enums/document-type.enum';
import {EnumType} from '@app/shared/enums/enum-type.enum';
import {RouteLink} from '@app/shared/enums/route-link.enum';
import {RefDomainType} from '@app/shared/enums/ref-domain-type.enum';
import {Validation} from '@app/shared/enums/validation.enum';

import {CommonDataService} from '@app/shared/services/common-data.service';
import {RestrictedSolutionManagerService} from '@app/shared/services/restricted-solution-manager.service';
import {TradingPartnerService} from '@app/shared/services/trading-partner.service';
import {CommonDocRepoService} from "@app/shared/services/common-doc-repo.service";
import {DTColumn} from '@app/shared/classes/common';
import {classToClass, plainToClass} from 'class-transformer';
import {SolutionManagerDataService} from '@app/solution-manager/solution-manager-data.service';
import {
  ActionStatusTypeEnum,
  CurrentTxnStatus,
  DisbursementType,
  FundingSourceStatus,
  GrowlType,
  PartnerPaymentTerms,
  PaymentTermName,
  PaymentTermType,
  PaymentType,
  PrimeNgConfig,
  RegExp12DigitInteger2DigitFraction,
  ShippingType,
  VendorPaymentMessages
} from '@app/shared';
import {AccountsPayableService} from "@app/finance/accounts-payable/accounts-payable.service";
import {HttpErrorResponse} from "@angular/common/http";
import {DwollaMessages} from "@app/shared/ach/dwolla-messages.enum";
import {Router} from "@angular/router";
import {AchDataService} from "@app/shared/ach/ach-data.service";
import {RoutingNumberInfo} from "@app/shared/ach/org-payment-transaction";
import {AchCustomer, AchCustomerSearchType} from "@app/shared/classes/ach-customer";


@Component({
  selector: 'app-org-payment-info',
  templateUrl: './org-payment-info.component.html',
  styleUrls: ['./org-payment-info.component.css'],
  providers: [SolutionManagerDataService, AccountsPayableService, AchDataService]
})

export class OrgPaymentInfoComponent implements OnInit, OnChanges {

  ApprovalStatusType: typeof ApprovalStatusType = ApprovalStatusType;
  CurrentTxnStatus = CurrentTxnStatus;
  RouteLink: typeof RouteLink = RouteLink;
  CheckPaymentType: typeof CheckPaymentType = CheckPaymentType;
  DisbursementType: typeof DisbursementType = DisbursementType;
  DocumentType: typeof DocumentType = DocumentType;
  OrgType: typeof OrgType = OrgType;
  PaymentTermName: typeof PaymentTermName = PaymentTermName;
  PaymentType: typeof PaymentType = PaymentType;
  PrimeNgConfig = PrimeNgConfig;
  RegExp12DigitInteger2DigitFraction = RegExp12DigitInteger2DigitFraction;
  ShippingType: typeof ShippingType = ShippingType;
  Validation: typeof Validation = Validation;
  VendorPaymentMessages: typeof VendorPaymentMessages = VendorPaymentMessages;

  creditCard: OrgCreditCard | PartnerCreditCard;
  ach: OrgAchDetail | PartnerAchDetail;
  payerAddressDropdown: SelectItem[];
  viewOptions: SelectItem[] = [{label: "Yes", value: true}, {label: "No", value: false}];
  signatureOptions: SelectItem[];
  showFAQDialog: boolean = false;
  openPaymentTermsDialog: boolean = false;     //dialog to show payment terms description
  loadHtml: boolean = false;                   //loads html page
  disabledViewPaymentTermIcon: boolean = true; //until payment term selected from dropdown, view icon is disabled
  securityCodeLength: number = 0;
  displayAddAchDetail: boolean = false;       // When set to true, display Add Ach detail

  shipTypesDropDown: SelectItem[];        //list of shipping/delivery types for dropdown like Ups,Fedex,etc
  paymentTypesDropDown: SelectItem[];     //list of payment types for dropdown like Credit, bank transfer,etc
  monthDropdown: SelectItem[];            //list of month codes for dropdown
  yearDropdown: SelectItem[];             //list of years for dropdown
  paymentTermsDropdown: SelectItem[];     //list of payment terms for dropdown like Net15, Net30,etc
  cardTypesDropDown: SelectItem[];        //list of card types for dropdown like amex,mastercard,etc
  paymentTerm: PaymentTerm;                    //object that is modeled in payment terms dialog

  achAccountTypesDropdown: SelectItem[];
  achDefaultPaymentTypeDropdown: SelectItem[];
  showAddEditDialog: boolean;
  selectedAchDetail: OrgAchDetail | PartnerAchDetail;
  FundingSourceStatus: typeof FundingSourceStatus = FundingSourceStatus;
  DwollaMessages: typeof DwollaMessages = DwollaMessages;
  achDetailsColumn: DTColumn[];
  saveOrUpdate: boolean;
  showAddEditFundingSourceDetailDialog: boolean;
  microDepositVerification: AchMicroDepositVerification = new AchMicroDepositVerification();
  maxFundingSources = 6;
  validRoutingNumber: boolean = true;       //To Disable Save/Update button, if routing number is invalid

  //Dwolla messages
  dwollaRemovedFundingSourceMsg: string;


  @Input() orgType: OrgType;
  @Input() isGovAgency: boolean;
  paymentSettings: PartnerPaymentSetting[];
  @Input() creditCardDetails: Array<OrgCreditCard | PartnerCreditCard>;
  @Input() achDetails: Array<OrgAchDetail | PartnerAchDetail> = [];
  @Input() tradingPartner: TradingPartner;

  @Output() handlerSave: EventEmitter<any> = new EventEmitter();

  constructor(private solMgrService: RestrictedSolutionManagerService, private commonDataService: CommonDataService, private formBuilder: FormBuilder, private growl: GrowlService,
              private docRepoService: CommonDocRepoService, private partnerService: TradingPartnerService, private user: UserService, private solutionManagerDataService: SolutionManagerDataService,
              private confirmationService: ConfirmationService, private accountsPayableService: AccountsPayableService, private httpError: HttpErrorMessageService, private router: Router,
              private achDataService: AchDataService) {
  }


  /**
   * @desc on load of page, detect the arrays and load empty objects to set the view
   */
  ngOnChanges() {
    if (this.creditCardDetails && this.creditCardDetails.length > 0) {
      this.creditCard = this.creditCardDetails[0];
    } else if (!this.creditCardDetails || this.creditCardDetails.length == 0) {
      if (this.orgType != OrgType.Org) {
        this.creditCard = new PartnerCreditCard();
      } else {
        this.creditCard = new OrgCreditCard();
      }
    }

    this.copyPaymentSettingsFromTradingPartner();

    if (this.orgType != OrgType.Org && this.paymentSettings && this.paymentSettings.length > 0) {
      this.disabledViewPaymentTermIcon = true;
    } else if (this.orgType != OrgType.Org && this.paymentSettings.length == 0) {
      this.paymentSettings.push(new PartnerPaymentSetting());
    }

    this.formBuilder = new FormBuilder();

    // since payment tab is a common component, setDefaultValue  should only invoke in customer UI
    if (this.isGovAgency == true || this.isGovAgency == false) {
      this.setDefaultValue(this.isGovAgency);
    }
    this.checkEnableNewFundingSource();
    this.populateDwollaMessages();
  }

  copyPaymentSettingsFromTradingPartner(): void {
    if (this.tradingPartner && this.tradingPartner.partnerPaymentSettings) {
      this.paymentSettings = this.tradingPartner.partnerPaymentSettings;
    }
  }

  ngOnInit() {
    this.buildDropdown();
    this.getPaymentTerms();
    this.populateDwollaMessages();
    this.setUpAchDTColumns();
  }

  /**
   * @desc build dropdown menus
   * @returns this.shipTypesDropDown - shipping/delivery types dropdown menu retrieved from backend
   *          this.paymentTypesDropDown -  payment method types dropdown menu retrieved from backend
   *          this.cardTypesDropDown -  payment card types dropdown menu retrieved from backend
   *          this.monthDropdown -  month codes dropdown menu retrieved from backend
   *          this.yearDropdown -  years dropdown menu retrieved from backend
   */
  buildDropdown(): void {
    forkJoin(
      this.commonDataService.getEnumPairAsDropdown(EnumType.Shipping),
      this.commonDataService.getEnumPairAsDropdown(EnumType.DisbursementType),
      this.commonDataService.getEnumPairAsDropdown(EnumType.CardType),
      this.commonDataService.getRefCodeAsDropdown(RefDomainType.Month, true),
      this.commonDataService.getRefCodeAsDropdown(RefDomainType.Year, true),
      this.solMgrService.getMainAndOtherOrgAddressAsDropDown(),
      this.commonDataService.getEnumPairAsDropdown(EnumType.AchAccountType),
      this.commonDataService.getEnumPairAsDropdown(EnumType.AchDefaultPaymentType)
    ).subscribe(res => {
      this.shipTypesDropDown = res[0];
      this.paymentTypesDropDown = res[1];

      // remove ACH if customer
      if (this.orgType == OrgType.Customer) {
        const index: number = this.paymentTypesDropDown.findIndex(options => options.value == DisbursementType.ACH);
        this.paymentTypesDropDown.splice(index, 1);
      }
      this.cardTypesDropDown = res[2];
      this.monthDropdown = res[3];
      this.yearDropdown = res[4];
      this.payerAddressDropdown = res[5];
      this.achAccountTypesDropdown = res[6];
      this.achDefaultPaymentTypeDropdown = res[7];
    });

    this.signatureOptions = [];
    this.signatureOptions.push({label: 'Text', value: true});
    this.signatureOptions.push({label: 'Image', value: false});
  }

  populateDwollaMessages(): void {
    this.dwollaRemovedFundingSourceMsg = DwollaMessages.RemovedFundingSourceUserMessage;
  }

  setUpAchDTColumns(): void {
    /**
     * @desc this method sets up the columns for the ptable
     */
    this.achDetailsColumn = [];
    this.achDetailsColumn.push({field: 'nickName', header: 'Nickname'});
    this.achDetailsColumn.push({field: 'bankName', header: 'Bank'});
    this.achDetailsColumn.push({field: 'accountName', header: 'Account Name'});
    this.achDetailsColumn.push({field: 'accountType', header: 'Account Type'});
    if (this.orgType == OrgType.Org) {
      this.achDetailsColumn.push({field: 'achDefaultPaymentType', header: 'Default Payment For'});
      this.achDetailsColumn.push({field: 'enableCheckPayment', header: 'Check Payments'});
    }
    if (this.orgType == OrgType.Vendor) {
      this.achDetailsColumn.push({field: 'current', header: 'Primary Pay'});
    }
    this.achDetailsColumn.push({field: 'action', header: 'Action'});
  }

  addAchDetailDisplay(): void {
    /**
     * @desc this method displays the add mod Dialog
     */
    if (this.orgType != OrgType.Org) {
      this.selectedAchDetail = new PartnerAchDetail();
      this.selectedAchDetail.current = true;
    } else {
      this.selectedAchDetail = new OrgAchDetail();
    }
    this.saveOrUpdate = true;
    this.showAddEditDialog = true;
    this.validRoutingNumber = false;
  }

  editAchDetailDisplay(node: PartnerAchDetail | OrgAchDetail): void {
    this.selectedAchDetail = classToClass(node);
    this.saveOrUpdate = false;
    this.showAddEditDialog = true;
  }

  validateRoutingNumber(routingNumber: string): void {

    this.achDataService.getIsValidRoutingNumber(routingNumber).subscribe(res => {
      const routingNumberInfo: RoutingNumberInfo = plainToClass(RoutingNumberInfo, res as RoutingNumberInfo);
      this.validRoutingNumber = routingNumberInfo.valid;
    });
  }

  cancelAchDetail(event?): void {
    this.showAddEditDialog = false;
  }

  addAchDetail(): void {
    this.selectedAchDetail.id = null;
    this.selectedAchDetail.version = 0;
    if (this.orgType != OrgType.Org) {
      this.selectedAchDetail['partnerId'] = this.tradingPartner.id;
    } else {
      this.selectedAchDetail['orgId'] = this.user.managingOrgId;
    }

    if (this.orgType == OrgType.Customer || this.orgType == OrgType.Vendor) {
      this.partnerService.saveAchDetail(this.selectedAchDetail).subscribe(res => {
        this.partnerService.getPartner(this.tradingPartner.id).subscribe(partnerResponse => {
          const tradingPartner: TradingPartner = plainToClass(TradingPartner, partnerResponse);
          this.achDetails = tradingPartner.partnerAchDetails;
          this.checkEnableNewFundingSource();
          this.showAddEditDialog = false;
          this.growl.add(GrowlType.Success, 'The Partner AchDetail updated.');
        });
      }, error => {
        //TODO: Check the best way to do it at an framework level
        if (error instanceof HttpErrorResponse) {
          if (error.status == 424) {
            this.growl.add(GrowlType.Warn, this.httpError.getErrorMessage(error));
          } else {
            this.showAddEditDialog = false;
            this.growl.add(GrowlType.Error, this.httpError.getErrorMessage(error));
          }
        }
      });

    } else if (this.orgType == OrgType.Org) {
      let isPaymentTypePresent = false;

      this.achDetails.forEach(achDetail => {
        if (achDetail.achDefaultPaymentType === this.selectedAchDetail.achDefaultPaymentType && achDetail.active) {
          isPaymentTypePresent = true;
        }
      });

      if (!isPaymentTypePresent) {
        this.solutionManagerDataService.saveAchDetail(this.selectedAchDetail).subscribe(res => {
          this.achDetails.push(Object.assign({}, plainToClass(OrgAchDetail, res)));
          this.checkEnableNewFundingSource();
          this.showAddEditDialog = false;
          this.handlerSave.emit(ActionStatusTypeEnum.PaymentUpdated);
        }, error => {
          //TODO: Check the best way to do it at an framework level
          if (error instanceof HttpErrorResponse) {
            if (error.status == 424) {
              this.showAddEditDialog = false;
              this.growl.add(GrowlType.Warn, this.httpError.getErrorMessage(error));
            } else {
              this.growl.add(GrowlType.Error, this.httpError.getErrorMessage(error));
            }
          }
        });
      } else {
        this.growl.add(GrowlType.Warn, 'Default payment type already present');
      }
    }


  }

  onClickEditAchDetail(): void {

    if (this.orgType == OrgType.Org) {
      this.confirmationService.confirm({
        key: 'updateAch',
        message: 'Updating the Ach information may require you to reverify the information through Microdeposits. Do you want to Continue?',
        accept: () => {
          this.editAchDetail();
        }
      });
    } else {
      const index = this.achDetails.findIndex(ach => this.selectedAchDetail.id == ach.id);
      if (index >= 0) {
        //Check if Primary pay is being modified as Non-primary pay
        const prevAch = this.achDetails[index];
        if (this.paymentSettings[0].preferPaymentType == DisbursementType.ACH && prevAch.current && !this.selectedAchDetail.current) {
          this.showAddEditDialog = false;
          this.growl.add(GrowlType.Error, VendorPaymentMessages.UpdatePrimaryPaytoNonPrimaryPay);
          return;
        }
      }
      this.editAchDetail();
    }
  }

  /**
   * Edit Ach Can be a pseudo Update (Set as inactive and create a new record) or a proper Update based on the transactions associated with the ach detail
   * Hence Fetch latest funding source information by making an additional call.
   */
  editAchDetail(): void {
    if (this.orgType == OrgType.Customer || this.orgType == OrgType.Vendor) {

      this.partnerService.updateAchDetail(this.selectedAchDetail).subscribe(res => {

        this.partnerService.getPartner(this.tradingPartner.id).subscribe(partnerResponse => {
          const tradingPartner: TradingPartner = plainToClass(TradingPartner, partnerResponse);
          //Set only the ach details and emit the event, to prevent deleteion of removed funding source
          this.achDetails = tradingPartner.partnerAchDetails;
          this.checkEnableNewFundingSource();
          this.showAddEditDialog = false;
          this.growl.add(GrowlType.Success, 'The Partner AchDetail updated');
        });

        //If there is a change in account number, routing number etc., A new Ach detail is being added, Hence fetch the details again
        //this.dataService.get

      }, error => {
        if (error instanceof HttpErrorResponse) {
          if (error.status == 424) {
            this.showAddEditDialog = false;
            //TODO: Remove the extra detail in error message
            this.growl.add(GrowlType.Warn, this.httpError.getErrorMessage(error));
          } else {
            this.growl.add(GrowlType.Error, this.httpError.getErrorMessage(error));
          }
        }
      });
    } else if (this.orgType == OrgType.Org) {
      this.solutionManagerDataService.updateAchDetail(this.selectedAchDetail).subscribe(res => {
        this.solutionManagerDataService.getOrgAchDetail(this.user.managingOrgId).subscribe(res => {
          this.achDetails = plainToClass(OrgAchDetail, res.list);
          this.checkEnableNewFundingSource();
          this.showAddEditDialog = false;
          this.growl.add(GrowlType.Success, 'The Org AchDetail updated');
        });
      }, error => {
        if (error instanceof HttpErrorResponse) {
          if (error.status == 424) {
            this.showAddEditDialog = false;
            //TODO: Remove the extra detail in error message
            this.growl.add(GrowlType.Warn, this.httpError.getErrorMessage(error));
          } else {
            this.growl.add(GrowlType.Error, this.httpError.getErrorMessage(error));
          }
        }
      });
    }
  }

  /**
   * @desc hits db to get list of payment terms for dropdown using customTermName and StandardTermName
   */
  getPaymentTerms(): void {
    this.copyPaymentSettingsFromTradingPartner();
    this.partnerService.getPaymentTerms().subscribe(res => {
      this.paymentTermsDropdown = [];
      let terms = plainToClass(PartnerPaymentTerms, res.list);
      if (this.orgType == OrgType.Customer) {
        terms = terms.filter(term => term.paymentTermType == PaymentTermType.Standard);
      }
      terms.forEach(term => {
        if (term.standardTermName == null) {
          this.paymentTermsDropdown.push({label: term.monthlyTermName, value: term});
        } else {
          this.paymentTermsDropdown.push({label: term.standardTermName, value: term});
        }
      });
      if (this.paymentSettings && this.paymentSettings[0] && this.paymentSettings[0].paymentTermsId) {
        this.paymentTerm = this.paymentTermsDropdown.find(pt => pt && pt.value.id == this.paymentSettings[0].paymentTermsId).value;
      }
      // calling in customer invoice when the dropdown values are missing on pageload
      if (this.isGovAgency == true || this.isGovAgency == false) {
        this.setDefaultValue(this.isGovAgency);
      }
    });
  }


  /**
   * @desc payment term selected form p-dropdown. if Standard term selected: show show view and fields, else shows custom view and fields
   * @param event user input in p-dropdown
   */
  onPaytermSelect(event): void {
    if (this.paymentTerm.id) {
      this.paymentSettings[0].paymentTermsId = this.paymentTerm.id;
    }
  }


  /**
   * @desc opens payment term dialog
   */
  openPaymentPopUp(): void {
    this.openPaymentTermsDialog = true;
  }

  /**
   * @desc closed payment term dialog
   */
  cancelPaymentTerms(): void {
    this.openPaymentTermsDialog = false;
  }


  /**
   * @desc used as a trigger to parent to hit save for Org/Customer. For Approved Vendor, save the payment settings
   */
  onClickSavePaymentSettings(): void {
    if (this.orgType == OrgType.Org) {
      this.handlerSave.emit(ActionStatusTypeEnum.PaymentUpdated);
    } else if (this.orgType == OrgType.Vendor || this.orgType == OrgType.Customer) {
      //Save Payment Settings individually, if Vendor is approved
      if (this.tradingPartner && this.tradingPartner.currentStatusName == ApprovalStatusType.Completed) {

        this.partnerService.updatePartnerPaymentSettings(this.tradingPartner.id, this.paymentSettings[0]).subscribe(res => {
          this.paymentSettings[0] = plainToClass(PartnerPaymentSetting, res as PartnerPaymentSetting);
          //Updating the partner payment settings can update Ach customer details, Hence make an explicit call to refresh the latest status
          this.achDataService.getAchCustomerForResource(this.tradingPartner.id, AchCustomerSearchType.Vendor).subscribe(res => {
            this.tradingPartner.achCustomerDetails = plainToClass(AchCustomer, res as AchCustomer);
          });
        });
      } else {
        this.handlerSave.emit(ActionStatusTypeEnum.PaymentUpdated);
      }
    }
  }


  /**
   * @desc process selected policy file data
   */
  handleSignatureImage(signatureImage: DocFile): void {
    if (signatureImage && signatureImage.data) {
      if (this.selectedAchDetail && this.selectedAchDetail.signatureImage && this.selectedAchDetail.signatureImage.id) {
        this.selectedAchDetail.signatureImage.data = signatureImage.data;
        this.selectedAchDetail.signatureImage.mimeType = signatureImage.mimeType;
        this.selectedAchDetail.signatureImage.fileName = signatureImage.fileName;
      } else {
        this.selectedAchDetail.signatureImage = signatureImage;
      }
    } else {
      console.error("selected file is null or empty");
    }
  }

  onClickDownload(ach: OrgAchDetail): void {
    this.docRepoService.getFileByIdThenSave(ach.signatureImage.id);
  }

  generateSampleCheck(): void {
    this.solMgrService.generateSampleCheck();
  }

  setDefaultValue(isGovAgency: boolean): void {
    // for a new customer who donot have paymentSetting
    if (!this.paymentSettings || this.paymentSettings.length == 0) {
      this.paymentSettings = [new PartnerPaymentSetting()];
    }
    if (this.paymentTermsDropdown && this.paymentTermsDropdown.length > 0) {

      if (!this.paymentSettings[0].id) {
        if (isGovAgency) {
          const index: number = this.paymentTermsDropdown.findIndex(data => data.value.paymentTermsName == this.PaymentTermName.Net30);
          if (index >= 0) {
            this.paymentTerm = this.paymentTermsDropdown[index].value as PaymentTerm;
            this.paymentSettings[0].paymentTermsId = this.paymentTerm.id;
          }
          this.paymentSettings[0].creditLimit = Validation.DefaultCreditLimitForGovAgency;
          this.paymentSettings[0].specialDiscount = 0;
          this.paymentSettings[0].preferPaymentType = this.DisbursementType.OtherElectronic;
          this.paymentSettings[0].preferDeliveryType = this.ShippingType.Electronic;

        } else {
          const index: number = this.paymentTermsDropdown.findIndex(data => data.value.paymentTermsName == this.PaymentTermName.Net30);
          if (index >= 0) {
            this.paymentTerm = this.paymentTermsDropdown[index].value as PaymentTerm;
            this.paymentSettings[0].paymentTermsId = this.paymentTerm.id;
          }

          this.paymentSettings[0].preferPaymentType = this.DisbursementType.OtherElectronic;
          this.paymentSettings[0].creditLimit = null;
          this.paymentSettings[0].specialDiscount = 0;
          this.paymentSettings[0].preferDeliveryType = this.ShippingType.Electronic;
        }
      }
    }
  }

  showAchFundingSourceDetail(row: OrgAchDetail | PartnerAchDetail): void {
    this.selectedAchDetail = classToClass(row);
    this.microDepositVerification = new AchMicroDepositVerification();
    this.showAddEditFundingSourceDetailDialog = true;

  }

  onClickDeleteAch(row: PartnerAchDetail | OrgAchDetail): void {
    this.confirmationService.confirm({
      key: 'deleteAch',
      message: 'Are you sure you want to remove the Ach Detail, You cannot make transactions with this account',
      accept: () => {
        this.deleteAchDetailDisplay(row);
      }
    });
  }

  /**
   * Checks if we can display "Add Ach Detail" button
   */
  checkEnableNewFundingSource(): void {

    const activeFundingSources = this.achDetails.filter(ach => ach.active);
    this.displayAddAchDetail = activeFundingSources.length < Validation.MaxNumOfAllowedAchAccounts;
  }

  /**
   * Delete Ach Can be a pseudo delete (Set as inactive) or a proper delete based on the transactions associated with the ach detail
   * Hence Fetch latest funding source information by making an additional call.
   * @param row
   */
  deleteAchDetailDisplay(row: PartnerAchDetail | OrgAchDetail): void {

    if (this.orgType == OrgType.Customer || this.orgType == OrgType.Vendor) {
      //Trading partner service to delete the funding source
      this.partnerService.deleteAchDetail(row).subscribe(res => {
        this.partnerService.getPartner(this.tradingPartner.id).subscribe(partnerResponse => {
          const tradingPartner: TradingPartner = plainToClass(TradingPartner, partnerResponse);
          //Set only the ach details and emit the event, to prevent deletion of removed funding source
          this.achDetails = tradingPartner.partnerAchDetails;
          this.checkEnableNewFundingSource();
          this.growl.add(GrowlType.Success, "The AchDetail deleted");
        });
      }, error => {
        if (error instanceof HttpErrorResponse) {
          if (error.status == 424) {
            this.growl.add(GrowlType.Warn, this.httpError.getErrorMessage(error));
          } else {
            this.growl.add(GrowlType.Error, this.httpError.getErrorMessage(error));
          }
        }
      });
    } else if (this.orgType == OrgType.Org) {
      this.solutionManagerDataService.deleteAchDetail(row).subscribe(res => {
        this.solutionManagerDataService.getOrgAchDetail(this.user.managingOrgId).subscribe(res => {
          this.achDetails = plainToClass(OrgAchDetail, res.list);
          this.checkEnableNewFundingSource();
          this.showAddEditDialog = false;
          this.growl.add(GrowlType.Success, 'The Org AchDetail deleted');
        });
      }, error => {
        if (error instanceof HttpErrorResponse) {
          if (error.status == 424) {
            this.growl.add(GrowlType.Warn, this.httpError.getErrorMessage(error));
          } else {
            this.growl.add(GrowlType.Error, this.httpError.getErrorMessage(error));
          }
        }
      });
    }
  }

  initiateMicroDepositVerification(selectedAchDetail: PartnerAchDetail | OrgAchDetail): void {

    this.partnerService.initiateMicroDepositVerification(selectedAchDetail).subscribe(res => {
      const index: number = this.achDetails.findIndex(achDetail => res.id === achDetail.id);
      if (index > -1) {
        this.achDetails[index] = plainToClass(OrgAchDetail, res);
        this.showAddEditFundingSourceDetailDialog = false;
      }
    });
    this.handlerSave.emit(ActionStatusTypeEnum.PaymentUpdated);
  }

  verifyMicroDeposit(selectedAchDetail: PartnerAchDetail | OrgAchDetail, microDepositVerification: AchMicroDepositVerification): void {
    this.partnerService.verifyMicroDeposit(selectedAchDetail, microDepositVerification).subscribe(res => {
      const index: number = this.achDetails.findIndex(achDetail => res.id === achDetail.id);
      if (index > -1) {
        this.achDetails[index] = plainToClass(OrgAchDetail, res);
        this.showAddEditFundingSourceDetailDialog = false;
      }
      this.handlerSave.emit(ActionStatusTypeEnum.PaymentUpdated);
    });
  }

  onClickCancelFundingSourceStatus(): void {
    this.showAddEditFundingSourceDetailDialog = false;
  }

  displayAchCustomerPopUp(event, op1: OverlayPanel): void {
    if (this.tradingPartner.achCustomerDetails) {
      op1.toggle(event);
    }
  }

  showInfoOnPaymentMethod(event, overlayPanel: OverlayPanel): void {
    overlayPanel.toggle(event);
  }
}
